import { PortOrAddress } from 'features/Directory'
import { ShipmentMode, ShipmentType } from 'features/ShipmentDetails'
import { compact, flatten, remove } from 'lodash-es'
import moment from 'moment'
import { ISO } from 'service/ISO'

import { BookingParty } from '../Incoterms/incotermsMeta'
import { LineItemInterface } from '../LineItem/LineItemInterface'
import { DemDetQuote } from './DemDetQuote'
import { PreOnCarriageMode } from './PreOnCarriageMode'
import { CarriageRate, Rate } from './Rate/Rate'
import { SailingSchedule } from './SailingSchedule'

type RouteType = 'preRoute' | 'mainRoute' | 'postRoute' | 'haulageRoute' | 'customsRoute' | 'depotRoute'
export type Route = RouteSegment & {
  segments: RouteSegment[]
}

export type Frequency = 'daily' | 'weekly'
export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
type AdditionalRemark = {
  type: 'AuthorityDuties' | 'MissingDefermentAccount' | 'T1CIFCosts'
  message: string
}
export type RouteSegment = {
  carrier_id?: string
  distance?: number
  duration?: number
  durationInCalendarDays?: number
  eta?: Date
  etd?: Date
  journey: {
    origin: PortOrAddress
    destination: PortOrAddress
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  scheduleReference?: any
  transportMode?: ShipmentMode | PreOnCarriageMode
  type?: RouteType
  routeSchedule?: SailingSchedule

  // lcl
  cutOff?: number
  daysOfDeparture?: DayOfWeek[]
  frequency?: Frequency

  // air
  airline?: string
  flightNumber?: string
  airlineData?: Airline[]
}

interface Airline {
  _id: string
  name: string
}

export enum QuoteSource {
  MAERSK_SPOT = 'MAERSK_SPOT',
  DEFAULT = 'DEFAULT',
}

interface BaseQuote {
  additionalCharges?: AdditionalRemark[]
  lineItems: LineItemInterface[]
  type?: string
  source?: QuoteSource
}

type VGMQuote = BaseQuote & {
  additionalCharges: AdditionalRemark[]
  lineItems: LineItemInterface[]
  portId: string
}

type VoucherQuote = BaseQuote & {
  additionalCharges: AdditionalRemark[]
  lineItems: LineItemInterface[]
  voucherId: string
}

export type RouteQuote = BaseQuote & {
  mode: ShipmentMode | PreOnCarriageMode
  rates: (Rate | CarriageRate)[]
  route: Route
}

export enum RateType {
  AGREED = 'agreed',
  GENERAL = 'general',
  HYBRID = 'hybrid',
}

export interface IQuoteOption {
  _id?: string
  internalIndex?: number
  sheet_id?: string

  totalDuration: number
  type: ShipmentType
  mode: ShipmentMode
  totalEUR: number
  handlingFeeLineItem?: LineItemInterface
  isManual?: boolean
  isNotFak: boolean
  rateType?: RateType
  bookingParty?: BookingParty
  incoTerm?: ISO.IncoTermsCode
  isBookingPartyAgent?: boolean
  lineItems: LineItemInterface[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  additionalCharges: any
  bookableUntil: Date
  insuranceQuote?: BaseQuote
  voucherQuote?: VoucherQuote

  originCustomsQuote?: BaseQuote
  originDemDetQuote?: DemDetQuote
  vgmQuote?: VGMQuote
  carbonOffsetQuote?: BaseQuote

  destinationDemDetQuote?: DemDetQuote
  destinationCustomsQuote?: BaseQuote

  routeQuotes: {
    preQuote?: RouteQuote
    mainQuote: RouteQuote
    postQuote?: RouteQuote
  }
}

// TODO set this somewhere in config later and make it configurable via TMS
const minCutOff = 10
const minETDBook = 12
const PRERUN_DURATION = 4 * 24 * 60 * 60 * 1000

export function optionSubjectToAvailability(option: IQuoteOption): boolean {
  const isD2X = option.routeQuotes.preQuote != null
  if (isD2X) {
    const etd = departureDate(option)
    const daysTo = Math.round(moment(etd).diff(new Date(), 'd', true))
    return daysTo >= 6
  }
  if (!option.routeQuotes.mainQuote.route.routeSchedule) {
    return true
  }
  const today = new Date()
  const { cutOff } = option.routeQuotes.mainQuote.route.routeSchedule
  let cutOffDate
  if (cutOff) {
    cutOffDate = moment(cutOff).subtract(minCutOff, 'days')
  } else {
    cutOffDate = moment(option.routeQuotes.mainQuote.route.routeSchedule.etd).subtract(minETDBook, 'days')
  }
  return today < cutOffDate.toDate()
}

/**
 * Decides wether an option should be shown based on ETD
 * Always shows manual options
 * Always shows LCL, as they do not have an ETD and can therefore not be filtered by it
 * @param option
 */
export function optionValid(option: IQuoteOption): boolean {
  const isD2X = option.routeQuotes.preQuote != null
  if (option.type === 'lcl' || option.isManual === true) {
    return true
  }
  if (isD2X) {
    const etd = departureDate(option)
    const daysTo = Math.round(moment(etd).diff(new Date(), 'd', true))
    return daysTo >= 3
  }
  return true
}

export function arrivalDate(option, ignorePreCarriageDuration?: boolean): Date {
  const momentObj = moment(departureDate(option, ignorePreCarriageDuration)).add(option.totalDuration)
  return momentObj.toDate()
}

export function departureDate(option: IQuoteOption, ignorePreCarriageDuration?: boolean): Date {
  const { etd } = option.routeQuotes.mainQuote.route
  // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'Date'.
  if (etd == null) return null

  if (option.routeQuotes.preQuote != null && !ignorePreCarriageDuration) {
    const preCarriageDuration = option.routeQuotes.preQuote.route.duration || PRERUN_DURATION
    return moment(etd).subtract(preCarriageDuration, 'ms').toDate()
  }
  return moment(etd).toDate()
}

// Sort line items according to their types
export function getLineItemsSortedByType(option: IQuoteOption): LineItemInterface[] {
  const {
    originCustomsQuote,
    destinationCustomsQuote,
    insuranceQuote,
    handlingFeeLineItem,
    voucherQuote,
    carbonOffsetQuote,
    routeQuotes: { preQuote, mainQuote, postQuote },
    vgmQuote,
  } = option

  const handlingLineItems = remove(mainQuote.lineItems, (lineItem) => lineItem.type === 'handling')
  // For FCL the HandlingLineItem is included in the Freight
  // For ManualQuotes we create a HandlingLineItem additionally
  if (
    isOptionFromMaerskSpot(option) ||
    (option.type === 'lcl' && option.isManual !== true && option.handlingFeeLineItem !== null)
  ) {
    // @ts-expect-error TS(2345) FIXME: Argument of type 'LineItemInterface | undefined' i... Remove this comment to see the full error message
    handlingLineItems.push(handlingFeeLineItem)
  }

  const lineItems = [
    preQuote?.lineItems,
    vgmQuote?.lineItems,
    mainQuote?.lineItems,
    postQuote?.lineItems,
    originCustomsQuote?.lineItems,
    destinationCustomsQuote?.lineItems,
    insuranceQuote?.lineItems,
    carbonOffsetQuote?.lineItems,
    voucherQuote?.lineItems,
    handlingLineItems,
  ]

  return compact(flatten(lineItems))
}

export const getNonZeroLineItemsFromOption = (quoteOption: IQuoteOption) =>
  getLineItemsSortedByType(quoteOption).filter((lineItem) => lineItem.unitPrice.value !== 0)

function getOptionSource(option: IQuoteOption): QuoteSource | void {
  // Currently we only have it in mainQuote.
  return option.routeQuotes.mainQuote.source
}

export function isOptionFromMaerskSpot(option: IQuoteOption): boolean {
  return getOptionSource(option) === QuoteSource.MAERSK_SPOT
}
